import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup, Spinner, Badge } from 'react-bootstrap';
import { getQuotePDFBase64 } from '../utils/pdfGenerator';
import { sendDocumentEmail } from '../utils/emailHelper';

/**
 * Modal genérico y modular para enviar comprobantes por correo electrónico.
 * Compatible con Presupuestos, Facturas, Remitos, Pedidos, Recibos, etc.
 * 
 * Props soportadas:
 * - show: boolean
 * - onHide: () => void
 * - title: string (ej: "Enviar Presupuesto por Email")
 * - documentType: string (ej: "Presupuesto", "Factura", "Remito")
 * - documentNumber: string (ej: "PRE-00000009")
 * - clientName: string (ej: "IBERPEL")
 * - defaultEmail: string
 * - defaultSubject: string
 * - defaultMessage: string
 * - attachmentName: string (ej: "Presupuesto_PRE-00000009.pdf")
 * - onGenerateAttachment: () => string (retorna base64 del PDF)
 * - onSend: async (payload) => boolean | { success: boolean }
 * 
 * Atajo para Presupuestos:
 * - quote, user, sellers (autocompleta todos los valores anteriores automáticamente)
 */
const SendEmailModal = ({
    show,
    onHide,
    title,
    documentType,
    documentNumber,
    clientName,
    defaultEmail,
    defaultSubject,
    defaultMessage,
    attachmentName,
    onGenerateAttachment,
    onSend,
    // Atajo para presupuestos
    quote,
    user,
    sellers
}) => {
    const [email, setEmail] = useState('');
    const [asunto, setAsunto] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [attachPDF, setAttachPDF] = useState(true);
    const [isSending, setIsSending] = useState(false);

    // Resolución de datos (Genéricos o Presupuesto)
    const resolvedDocType = documentType || (quote ? 'Presupuesto' : 'Comprobante');
    const resolvedDocNumber = documentNumber || (quote ? (quote.n_presu ? `PRE-${String(quote.n_presu).padStart(8, '0')}` : `PRE-${String(quote.id || '0').padStart(8, '0')}`) : 'COMP-00000000');
    const resolvedClientName = clientName || (quote?.d_razon ? quote.d_razon.trim() : 'Cliente General');
    const empresaNombre = user?.empresa_activa?.nombre || 'SISGECOM';

    // Precargar datos al abrir
    useEffect(() => {
        if (show) {
            const initialEmail = defaultEmail !== undefined 
                ? defaultEmail 
                : (quote?.cliente_d_email || quote?.d_email || '');
            
            const initialSubject = defaultSubject !== undefined
                ? defaultSubject
                : `${resolvedDocType} Nº ${resolvedDocNumber} - ${empresaNombre}`;

            setEmail(initialEmail);
            setAsunto(initialSubject);
            setMensaje(defaultMessage || '');
            setAttachPDF(true);
            setIsSending(false);
        }
    }, [show, quote, defaultEmail, defaultSubject, defaultMessage, resolvedDocType, resolvedDocNumber, empresaNombre]);

    const handleSend = async (e) => {
        e.preventDefault();

        if (!email || !email.trim()) {
            return;
        }

        setIsSending(true);

        try {
            // 1. Generar adjunto si está tildado
            let pdfBase64 = null;
            if (attachPDF) {
                if (typeof onGenerateAttachment === 'function') {
                    pdfBase64 = await onGenerateAttachment();
                } else if (quote) {
                    pdfBase64 = getQuotePDFBase64(quote, user, sellers);
                }
            }

            // 2. Ejecutar handler personalizado o acción por defecto para presupuestos
            if (typeof onSend === 'function') {
                const res = await onSend({
                    email: email.trim(),
                    asunto: asunto.trim(),
                    mensaje: mensaje.trim(),
                    pdf_base64: pdfBase64
                });
                if (res?.success || res === true) {
                    onHide();
                }
            } else if (quote) {
                const quoteId = quote.id || quote.n_presu;
                const companyId = user?.empresa_activa?.c_nuges || user?.empresa_activa?.id || 2;

                const res = await sendDocumentEmail({
                    url: `http://127.0.0.1:8000/api/presupuestos/${quoteId}/enviar-email/`,
                    email: email.trim(),
                    asunto: asunto.trim(),
                    mensaje: mensaje.trim(),
                    pdfBase64: pdfBase64,
                    companyId: companyId,
                    successMessage: 'El presupuesto se envió correctamente al cliente.'
                });

                if (res.success) {
                    onHide();
                }
            }
        } catch (error) {
            console.error('Error enviando email:', error);
        } finally {
            setIsSending(false);
        }
    };

    const resolvedAttachName = attachmentName || `${resolvedDocNumber}.pdf`;

    return (
        <Modal show={show} onHide={onHide} centered backdrop="static">
            <Modal.Header closeButton className="bg-primary text-white py-2">
                <Modal.Title className="fs-6 d-flex align-items-center">
                    <i className="bi bi-envelope-fill me-2"></i>
                    <span>{title || `Enviar ${resolvedDocType} por Email`}</span>
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSend}>
                <Modal.Body className="p-3">
                    <div className="bg-light p-2 rounded border mb-3 small">
                        <div className="d-flex justify-content-between">
                            <span className="text-muted">Comprobante:</span>
                            <strong className="text-primary">{resolvedDocNumber}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span className="text-muted">Cliente:</span>
                            <strong className="text-dark text-truncate ms-2">{resolvedClientName}</strong>
                        </div>
                    </div>

                    <Form.Group className="mb-2">
                        <Form.Label className="small fw-bold text-dark mb-1">
                            Destinatario(s) <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup size="sm">
                            <InputGroup.Text className="bg-light text-muted">
                                <i className="bi bi-person-at"></i>
                            </InputGroup.Text>
                            <Form.Control 
                                type="text"
                                placeholder="ej: cliente@gmail.com (separe con coma para múltiples)"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isSending}
                                required
                            />
                        </InputGroup>
                        <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Obtenido de la ficha del cliente. Puede editarlo o agregar correos separados por coma.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label className="small fw-bold text-dark mb-1">
                            Asunto
                        </Form.Label>
                        <Form.Control 
                            size="sm"
                            type="text"
                            value={asunto}
                            onChange={(e) => setAsunto(e.target.value)}
                            disabled={isSending}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-dark mb-1">
                            Mensaje personalizado (Opcional)
                        </Form.Label>
                        <Form.Control 
                            size="sm"
                            as="textarea"
                            rows={3}
                            placeholder="Escriba aquí una nota para el cliente que se incluirá en el cuerpo del correo..."
                            value={mensaje}
                            onChange={(e) => setMensaje(e.target.value)}
                            disabled={isSending}
                        />
                    </Form.Group>

                    <div className="bg-light p-2 rounded border d-flex align-items-center justify-content-between">
                        <Form.Check 
                            type="checkbox"
                            id="attach-pdf-check"
                            label={<span className="small fw-semibold text-dark">Adjuntar {resolvedDocType} en PDF</span>}
                            checked={attachPDF}
                            onChange={(e) => setAttachPDF(e.target.checked)}
                            disabled={isSending}
                        />
                        {attachPDF && (
                            <Badge bg="danger-subtle" text="danger" className="border border-danger">
                                <i className="bi bi-file-earmark-pdf me-1"></i>{resolvedAttachName}
                            </Badge>
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer className="bg-light py-2 d-flex justify-content-between">
                    <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={onHide}
                        disabled={isSending}
                        style={{ borderRadius: '6px' }}
                    >
                        <i className="bi bi-x-circle me-1"></i> Cancelar
                    </Button>
                    <Button 
                        variant="primary" 
                        size="sm" 
                        type="submit"
                        disabled={isSending}
                        className="fw-semibold shadow-sm"
                        style={{ borderRadius: '6px' }}
                    >
                        {isSending ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Enviando correo...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-send me-1"></i> Enviar Correo
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default SendEmailModal;
